/**
 * OsScanEntry — OS-INV-12 (navigation complement)
 *
 * Route: /os/scan  (PIN-gated)
 *
 * A minimal code-entry / location-picker page. Autofocused text input accepts
 * a typed or scanner-wedge item code. Submitting navigates to
 * /os/scan/:code/:location.
 *
 * Scanner-wedge compatible: the input is focused on mount; Enter submits.
 * The location picker is shown after a code is entered so a scan + tap is
 * enough to land on the right item page.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { scanDestination } from '../utils/scanDestination';
import { useScanListener } from '../hooks/useScanListener';

export const OsScanEntry: React.FC = () => {
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'code' | 'location'>('code');
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  // MADE-290 / MADE-292: Fallback scan listener — catches scans when the input loses focus
  useScanListener(
    useCallback((value: string) => {
      const destination = scanDestination(value);
      if (destination) navigate(destination);
    }, [navigate]),
    // MADE-292 fix: also route count-session QRs when the input is not focused
    useCallback((sessionId: string) => {
      // sessionId arrives already validated to 32-hex from useScanListener
      navigate(`/os/count/${sessionId.toLowerCase()}`);
    }, [navigate]),
  );

  useEffect(() => {
    inputRef.current?.focus();
  }, [step]);

  const handleCodeSubmit = () => {
    const trimmed = code.trim();
    if (!trimmed) return;

    // MADE-290: Wedge-mode QR codes output the full URL rather than just the
    // item code. Detect this pattern and navigate directly, bypassing the
    // location picker (the location is already encoded in the URL).
    const destination = scanDestination(trimmed);
    if (destination) {
      navigate(destination);
      return;
    }

    setStep('location');
  };

  const handleLocation = (loc: 'working' | 'storeroom') => {
    navigate(`/os/scan/${encodeURIComponent(code.trim())}/${loc}`);
  };

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        bgcolor: '#f4f6fb',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        pt: 8,
        px: 2,
      }}
    >
      <Paper elevation={3} sx={{ width: '100%', maxWidth: 400, p: 3, borderRadius: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <QrCodeScannerIcon sx={{ fontSize: 48, color: '#001550', mb: 1 }} />
          <Typography variant="h6" fontWeight={700} color="#001550">
            {step === 'code' ? 'Enter Item Code' : 'Select Location'}
          </Typography>
          {step === 'code' && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Type or scan an item code
            </Typography>
          )}
          {step === 'location' && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              <strong>{code.trim()}</strong> — which location?
            </Typography>
          )}
        </Box>

        {step === 'code' && (
          <>
            <TextField
              inputRef={inputRef}
              label="Item code"
              value={code}
              onChange={e => setCode(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCodeSubmit()}
              fullWidth
              autoComplete="off"
              inputProps={{ 'aria-label': 'Item code', inputMode: 'numeric' }}
              sx={{ mb: 2 }}
            />
            <Button
              fullWidth
              variant="contained"
              disabled={!code.trim()}
              onClick={handleCodeSubmit}
              sx={{ py: 1.5, fontWeight: 700, bgcolor: '#001550', '&:hover': { bgcolor: '#002680' } }}
            >
              Continue
            </Button>
          </>
        )}

        {step === 'location' && (
          <Stack spacing={2}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => handleLocation('working')}
              sx={{ py: 2, fontWeight: 700, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}
            >
              Working Inventory
            </Button>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => handleLocation('storeroom')}
              sx={{ py: 2, fontWeight: 700, bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
            >
              Store Room
            </Button>
            <Button
              variant="text"
              onClick={() => { setStep('code'); }}
              sx={{ color: 'text.secondary' }}
            >
              ← Back
            </Button>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};
